import React, { useEffect, useMemo, useState } from 'react';
import { useBranding } from '../context/BrandingContext';

const DEFAULT_MARK_LOGO = '/logo-mark.svg';

type ImageLoading = 'eager' | 'lazy';
type ImageDecoding = 'sync' | 'async' | 'auto';

interface BrandLogoProps {
  alt?: string;
  className?: string;
  fallbackIconClassName?: string;
  loading?: ImageLoading;
  decoding?: ImageDecoding;
  preferMark?: boolean;
  src?: string;
}

const shouldPreferMark = (value: string) => /(^|\/)logo\.svg(\?.*)?$/i.test(value);
const isEmpty = (value?: string) => !value || value.trim() === '';

const getInitials = (companyName?: string) => {
  const base = String(companyName || '').trim();
  if (!base) return 'PC';

  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return parts[0].slice(0, 2).toUpperCase() || 'PC';
};

const BrandLogo: React.FC<BrandLogoProps> = ({
  alt,
  className = 'w-full h-full object-contain',
  fallbackIconClassName = 'w-full h-full rounded-[inherit] bg-white text-slate-900 text-xs font-bold tracking-wide flex items-center justify-center',
  loading = 'lazy',
  decoding = 'async',
  preferMark = false,
  src,
}) => {
  const { branding } = useBranding();

  const explicitSrc = src?.trim();
  const brandingSrc = branding.logoUrl?.trim();
  const hasCustomBrandLogo = !isEmpty(brandingSrc) && !shouldPreferMark(brandingSrc || '');
  const shouldUseInitialsFallback = !explicitSrc && !hasCustomBrandLogo;
  const initials = getInitials(branding.companyName);

  const preferredSrc = useMemo(() => {
    if (shouldUseInitialsFallback) return '';
    const base = (explicitSrc || brandingSrc || DEFAULT_MARK_LOGO).trim();
    if (preferMark && shouldPreferMark(base)) return DEFAULT_MARK_LOGO;
    return base;
  }, [brandingSrc, explicitSrc, preferMark, shouldUseInitialsFallback]);

  const [resolvedSrc, setResolvedSrc] = useState(preferredSrc);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setResolvedSrc(preferredSrc);
    setFailed(false);
  }, [preferredSrc]);

  const handleError = () => {
    setFailed(true);
  };

  if (shouldUseInitialsFallback || failed || !resolvedSrc) {
    return (
      <span className={fallbackIconClassName} aria-label={alt || `${branding.companyName} initials`}>
        {initials}
      </span>
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt || `${branding.companyName} logo`}
      className={className}
      loading={loading}
      decoding={decoding}
      onError={handleError}
      draggable={false}
    />
  );
};

export default BrandLogo;
